import type { ReactElement } from 'react';
import React, { useCallback, useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { toInteger } from 'lodash';
import type { SubmitHandler } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import type { z } from 'zod';
import Alert from 'atom-components/alert';
import LoadingErrorAlert from 'feature-components/loading-error-alert';
import SaveButtons from 'feature-components/mark-response/mark-response-form/fields/save-buttons';
import useSaveHandler from 'feature-components/mark-response/mark-response-form/hooks/use-save-handler';
import MarkSubmission from 'feature-components/mark-response/mark-response-form/mark-submission';
import ConfirmFlagForAttentionModal, {
  shouldAskToFlagForAttention
} from 'feature-components/mark-response/mark-response-form/modals/confirm-flag-for-attention-modal';
import ConfirmMarkCreationModal, {
  shouldCheckIfOtherMarkingHasOccurred
} from 'feature-components/mark-response/mark-response-form/modals/confirm-mark-creation-modal';
import ConfirmMarkerChangeModal, {
  shouldAskToChangeActiveMarker
} from 'feature-components/mark-response/mark-response-form/modals/confirm-marker-change-modal';
import ConfirmSubmitForReviewModal, {
  shouldAskToSubmitForReview
} from 'feature-components/mark-response/mark-response-form/modals/confirm-submit-for-review-modal';
import QuestionCategories from 'feature-components/mark-response/mark-response-form/question-categories';
import type { UiCategory } from 'hooks/marking/ui-categories-builder';
import useAllocationHandler from 'hooks/marking/use-allocation-handler';
import { countMessages, isAnActiveMarkerOfResponse } from 'pages/marking/helpers/marker.helpers';
import { marksOfResponseParams } from 'pages/marking/helpers/request-params.helpers';
import { type AuthorisableMark } from 'policies/entities/mark';
import MarkPolicy from 'policies/mark.policy';
import type { MeCustomerUser } from 'services/assist-api/entities/me/me';
import type { BaseTag } from 'services/assist-api/entities/tag/tags';
import { useTestInstanceResponseMarks } from 'services/assist-api/entities/test-instance-response-mark/hooks';
import type { ManageMarkingMark } from 'services/assist-api/entities/test-instance-response-mark/types';
import {
  MarkStatus,
  type TestInstanceResponseMarkWithItems
} from 'services/assist-api/entities/test-instance-response-mark/types';
import type { TestInstanceResponseMarkerWithCustomerUser } from 'services/assist-api/entities/test-instance-response-marker/types';
import { AssistQueryClient } from 'services/assist-api/query-client';
import { usePageAccess } from 'services/providers/page-access.provider';
import MarkAdministration from './mark-administration';
import { loadDefaultValues } from './mark-response-form.helpers';
import {
  draftMarkResponseFormSchema,
  submitMarkResponseFormSchema,
  unrefinedMarkResponseFormSchema
} from './mark-response-form.schema';
import type { MarkResponseFormValues } from './mark-response-form.schema';

type Props = {
  onSave: () => void;
  responseId: number;
  rubricId: number;
  categories: UiCategory[];
  attentionReasons: BaseTag[];
  authorisableMark: AuthorisableMark;
  currentOrganisationUser: MeCustomerUser;
  currentUserMarker: TestInstanceResponseMarkerWithCustomerUser | undefined;
  activeMarker?: TestInstanceResponseMarkerWithCustomerUser;
  mark?: TestInstanceResponseMarkWithItems;
  oldMark?: TestInstanceResponseMarkWithItems;
};

export default function MarkResponseForm({
  onSave,
  responseId,
  rubricId,
  categories,
  attentionReasons,
  authorisableMark,
  currentOrganisationUser,
  currentUserMarker,
  activeMarker,
  mark,
  oldMark
}: Props): ReactElement {
  const { authorisableUser } = usePageAccess();
  const canEditMark = MarkPolicy.edit(authorisableUser, authorisableMark);
  const canAdministrateMark = MarkPolicy.administrate(authorisableUser, authorisableMark);
  const canMarkIndependently = MarkPolicy.markIndependently(authorisableUser, authorisableMark);
  const canOnlyMarkIndependently = canMarkIndependently && !canAdministrateMark;

  enum Modal {
    ConfirmMarkerChange = 'confirmMarkerChange',
    ConfirmMarkCreation = 'confirmMarkCreation',
    ConfirmFlagForAttention = 'confirmFlagForAttention',
    ConfirmSubmitForReview = 'confirmSubmitForReview'
  }
  const [currentModal, setCurrentModal] = useState<Modal | undefined>(undefined);
  const [hasMarkerChanged, setHasMarkerChanged] = useState<boolean>(false);

  const defaultValues: MarkResponseFormValues = loadDefaultValues(
    categories,
    attentionReasons,
    currentUserMarker?.id,
    activeMarker?.id,
    mark,
    oldMark
  );

  const draftResolver = zodResolver<z.ZodType<Partial<MarkResponseFormValues>>>(
    draftMarkResponseFormSchema
  );

  const submitResolver = zodResolver<z.ZodType<Partial<MarkResponseFormValues>>>(
    submitMarkResponseFormSchema(defaultValues.status)
  );

  const methods = useForm({
    mode: 'onTouched',
    resolver: (...args) => {
      if (args[0].submitAction === 'saveDraftMarking') {
        return draftResolver(...args);
      }
      return submitResolver(...args);
    },
    defaultValues
  });
  const { handleSubmit, watch, setValue } = methods;

  const watchedCurrentUserMarkerId = watch(
    unrefinedMarkResponseFormSchema.keyof().Enum.currentUserMarkerId
  );
  const { assignCurrentUserToMark, isAllocating } = useAllocationHandler(
    responseId,
    currentOrganisationUser.id,
    (markerId: number | undefined) => {
      setValue(unrefinedMarkResponseFormSchema.keyof().Enum.currentUserMarkerId, markerId);
      setValue(unrefinedMarkResponseFormSchema.keyof().Enum.activeMarkerId, markerId);
    }
  );
  const { updateTestInstanceResponseMark, createTestInstanceResponseMark, isSaving } =
    useSaveHandler(onSave, responseId, rubricId, defaultValues, mark);

  /**
   * Creates or updates a mark and any mark items that have changed, also allocating/assigning the
   * current organisation user to be the active marker if they are an independent marker.
   */
  const saveTestInstanceResponseMark = useCallback(
    async (formValues: MarkResponseFormValues) => {
      if (mark) {
        updateTestInstanceResponseMark(formValues);
      } else {
        createTestInstanceResponseMark(formValues);
      }
    },
    [createTestInstanceResponseMark, mark, updateTestInstanceResponseMark]
  );

  // Just before saving (after the Confirm Marker Change modal has been confirmed), this query can
  // refetch marks and mark items linked to the response to determine if the active marker has
  // marked anything.
  const watchedOtherMarkingKnown = watch('otherMarkingKnown');
  const marksQuery = useTestInstanceResponseMarks<ManageMarkingMark>(
    marksOfResponseParams(responseId),
    { enabled: !watchedOtherMarkingKnown }
  );

  const watchedActiveMarkerId = watch('activeMarkerId');
  /**
   * When creating a new mark, check if the active marker is already marking any marks or mark items
   * of the response. In the event the active marker has already done marking against the response,
   * creating a new mark may be unintentional. The marker should be warned and given a choice to
   * abort saving.
   */
  const checkIfOtherMarkingHasOccurred = useCallback(async (): Promise<boolean> => {
    // Clear the cache to ensure that the marks of the response are refetched from the API
    // if the marker submits the form again:
    await AssistQueryClient.invalidateQueries(['test-instance-response-marks']);

    // Trigger the marks query to run again now that the cache has been cleared:
    const latestMarksQuery = await marksQuery.refetch();

    // Set the Other Marking Known flag to true so that when the modal is confirmed, this check
    // won't be triggered again.
    setValue('otherMarkingKnown', true);

    return isAnActiveMarkerOfResponse(
      toInteger(watchedActiveMarkerId),
      latestMarksQuery.data?.json || []
    );
  }, [marksQuery, setValue, watchedActiveMarkerId]);

  const onSubmit: SubmitHandler<MarkResponseFormValues> = useCallback(
    async (formValues): Promise<boolean> => {
      // Automatically set the independent (only) markers to be the active marker before saving (if
      // they are not already):
      if (
        canOnlyMarkIndependently &&
        (!formValues.currentUserMarkerId ||
          formValues.activeMarkerId !== formValues.currentUserMarkerId)
      ) {
        await assignCurrentUserToMark();

        // Don't submit the form straight away. Use hasMarkerChanged flag to wait until the
        // useEffect has confirmed that the active marker ID is up to date in formValues:
        setHasMarkerChanged(true);
        return false;
      }
      // Provide the option for the current user to set themselves as the active marker:
      if (
        shouldAskToChangeActiveMarker(
          canAdministrateMark,
          canMarkIndependently,
          defaultValues,
          formValues
        )
      ) {
        setCurrentModal(Modal.ConfirmMarkerChange);
        return false;
      }
      // Provide the option to the marker to flag for attention if attention reasons have been added
      // or the comment has changed (for either save action):
      if (shouldAskToFlagForAttention(canAdministrateMark, defaultValues, formValues)) {
        setCurrentModal(Modal.ConfirmFlagForAttention);
        return false;
      }
      // Provide the option to cancel if the marker is about to submit their mark for review:
      if (shouldAskToSubmitForReview(canAdministrateMark, formValues)) {
        setCurrentModal(Modal.ConfirmSubmitForReview);
        return false;
      }
      // Provide the option to cancel if the marker has already marked the response:
      if (shouldCheckIfOtherMarkingHasOccurred(mark, formValues)) {
        const otherMarkingHasOccurred = await checkIfOtherMarkingHasOccurred();
        if (otherMarkingHasOccurred) {
          setCurrentModal(Modal.ConfirmMarkCreation);
          return false;
        }
      }

      // When updating a mark, or we've already checked for other marking, it is safe to proceed
      // with saving straight away, rather than checking through other marking of the response:
      saveTestInstanceResponseMark(formValues);
      return true;
    },
    [
      canOnlyMarkIndependently,
      canAdministrateMark,
      canMarkIndependently,
      defaultValues,
      mark,
      saveTestInstanceResponseMark,
      assignCurrentUserToMark,
      Modal.ConfirmMarkerChange,
      Modal.ConfirmFlagForAttention,
      Modal.ConfirmSubmitForReview,
      Modal.ConfirmMarkCreation,
      checkIfOtherMarkingHasOccurred
    ]
  );

  useEffect(() => {
    if (
      hasMarkerChanged &&
      watchedCurrentUserMarkerId !== undefined &&
      watchedCurrentUserMarkerId === watchedActiveMarkerId
    ) {
      setHasMarkerChanged(false);

      // Re-trigger the form submission to ensure that the active marker ID form value is up to
      // date before saving an independent marker's mark (since the submit handler only contains a
      // copy of formValues it will be out of date):
      handleSubmit(onSubmit)();
    }
  }, [handleSubmit, hasMarkerChanged, onSubmit, watchedActiveMarkerId, watchedCurrentUserMarkerId]);

  const triggerFormSubmission = useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  const { isError } = marksQuery;
  if (isError) {
    return <LoadingErrorAlert queries={[marksQuery]} />;
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ConfirmMarkerChangeModal
          isOpen={currentModal === Modal.ConfirmMarkerChange}
          onClose={(): void => setCurrentModal(undefined)}
          onConfirm={(): void => {
            setCurrentModal(undefined);
            triggerFormSubmission();
          }}
          responseId={responseId}
          currentOrganisationUser={currentOrganisationUser}
        />
        <ConfirmFlagForAttentionModal
          isOpen={currentModal === Modal.ConfirmFlagForAttention}
          onClose={(): void => {
            setValue('hasAskedToFlagForAttention', true);
            setCurrentModal(undefined);

            // Continue saving if "No Review Needed" is chosen. This will allow the marker to be
            // able to submit the mark after changing the attention reasons or comment without it
            // being forced into review. It is the responsibility of the marker to confirm that
            // the mark should be flagged for attention if it does need to be reviewed.
            triggerFormSubmission();
          }}
          onConfirm={(): void => {
            setValue('hasAskedToFlagForAttention', true);
            setValue('flagForAttention', true);
            setCurrentModal(undefined);
            triggerFormSubmission();
          }}
          defaultValues={defaultValues}
          attentionReasons={attentionReasons}
        />
        <ConfirmSubmitForReviewModal
          isOpen={currentModal === Modal.ConfirmSubmitForReview}
          onClose={(): void => setCurrentModal(undefined)}
          onConfirm={(): void => {
            setValue('submitForReview', true);
            setCurrentModal(undefined);
            triggerFormSubmission();
          }}
        />
        <ConfirmMarkCreationModal
          isOpen={currentModal === Modal.ConfirmMarkCreation}
          onClose={(): void => {
            // When the marker chooses not to confirm the mark creation, setting the other marking
            // known status back to false will force the query to be refetched if the marker tries
            // to submit again. This helps in cases where there is a delay between submission
            // attempts and the active marker changes in the interm or marks further work in the
            // background:
            setValue('otherMarkingKnown', false);
            setCurrentModal(undefined);
          }}
          onConfirm={(): void => {
            setCurrentModal(undefined);
            triggerFormSubmission();
          }}
          responseId={responseId}
          marksOfResponse={marksQuery.data?.json || []}
        />
        {marksQuery.isSuccess &&
          isAnActiveMarkerOfResponse(
            toInteger(watchedActiveMarkerId),
            marksQuery.data?.json || []
          ) && (
            <Alert variant="warning">
              {watchedActiveMarkerId === watchedCurrentUserMarkerId
                ? 'You have'
                : 'The active marker has'}{' '}
              already marked{' '}
              {countMessages(toInteger(watchedActiveMarkerId), marksQuery.data?.json || [])} of this
              response (<b>#{responseId}</b>).
            </Alert>
          )}
        <QuestionCategories
          categories={categories}
          authorisableMark={authorisableMark}
          defaultValues={defaultValues}
        />
        {/**
         * Show Save Buttons directly (rather than the whole Mark Submission component) for
         * independent markers who cannot administrate in order to simplify their workflow .
         */}
        {canOnlyMarkIndependently ? (
          <SaveButtons
            markStatus={mark?.status || MarkStatus.Assigned}
            canEditMark={canEditMark}
            canAdministrateMark={canAdministrateMark}
            isSavingOrAllocating={isSaving || isAllocating}
          />
        ) : (
          <>
            <MarkAdministration
              markStatus={mark?.status || MarkStatus.Assigned}
              canAdministrateMark={canAdministrateMark}
            />
            <MarkSubmission
              activeReviewerId={mark?.activeReviewer?.id}
              markStatus={mark?.status || MarkStatus.Assigned}
              attentionReasons={attentionReasons}
              canEditMark={canEditMark}
              canAdministrateMark={canAdministrateMark}
              isSavingOrAllocating={isSaving || isAllocating}
            />
          </>
        )}
      </form>
    </FormProvider>
  );
}
